import React from 'react'
import { Box, MenuItem, Select, TextField } from '@mui/material'
import { styled } from '@mui/material/styles'
import {
  SectionTitle,
  SectionSubtitle,
  Row,
  RowText,
  RowLabel,
} from '../../Profile/profile-styles'
import { MovieceptionSwitch } from '../../../components/Buttons/movieception-buttons'
import {
  SettingsResponse,
  NotificationsSettings,
} from '../../../client/settings-api'

const DEFAULT_QUIET_HOURS: NotificationsSettings['quietHours'] = {
  enabled: false,
  start: '22:00',
  end: '08:00',
}

const SectionTitleRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: theme.spacing(0.5),
}))

const InlineSectionTitle = styled(SectionTitle)({
  marginBottom: 0,
})

const FrequencySelect = styled(Select<string>)({
  minWidth: 110,
})

const QuietHoursRange = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
}))

interface Props {
  settings: SettingsResponse | null
  isDisabled: boolean
  onChange: <K extends keyof NotificationsSettings>(
    key: K,
    value: NotificationsSettings[K],
  ) => void
  onToggleAll: (value: boolean) => void
  onTopLevelToggle: (
    key: 'activityReminders' | 'recapAndMilestones',
    value: boolean,
  ) => void
}

export const NotificationsSection: React.FC<Props> = ({
  settings,
  isDisabled,
  onChange,
  onToggleAll,
  onTopLevelToggle,
}) => {
  // Master switch: when off the server sends nothing, so the per-type rows lock too.
  const masterOn = settings?.notifications.emailNotifications ?? false
  const typeDisabled = isDisabled || !masterOn
  // Older cached settings may predate these fields.
  const quietHours = settings?.notifications.quietHours ?? DEFAULT_QUIET_HOURS
  // "All emails" = master + every per-type flag, including the top-level
  // columns. Push notifications are device-managed on mobile and not part
  // of the web settings.
  const allOn =
    masterOn &&
    (settings?.notifications.newEpisodeAlerts ?? false) &&
    (settings?.notifications.newSeasonAlerts ?? false) &&
    (settings?.notifications.recommendations ?? false) &&
    (settings?.notifications.newReleases ?? false) &&
    (settings?.notifications.commentReplies ?? false) &&
    (settings?.activityReminders ?? false) &&
    (settings?.recapAndMilestones ?? false)

  return (
    <React.Fragment>
      <Box>
        <SectionTitleRow>
          <InlineSectionTitle>Notifications</InlineSectionTitle>
        </SectionTitleRow>
        <SectionSubtitle>
          Choose what you want to hear about from Movieception.
        </SectionSubtitle>

        <Row>
          <RowText>
            <RowLabel>All Emails On / Off</RowLabel>
          </RowText>
          <MovieceptionSwitch
            checked={allOn}
            onChange={(e) => onToggleAll(e.target.checked)}
            disabled={isDisabled}
          />
        </Row>
        <Row>
          <RowText>
            <RowLabel>Email Notifications</RowLabel>
          </RowText>
          <MovieceptionSwitch
            checked={masterOn}
            onChange={(e) => onChange('emailNotifications', e.target.checked)}
            disabled={isDisabled}
          />
        </Row>
        <Row>
          <RowText>
            <RowLabel>New Episode Alerts</RowLabel>
          </RowText>
          <MovieceptionSwitch
            checked={settings?.notifications.newEpisodeAlerts ?? false}
            onChange={(e) => onChange('newEpisodeAlerts', e.target.checked)}
            disabled={typeDisabled}
          />
        </Row>
        <Row>
          <RowText>
            <RowLabel>New Season Alerts</RowLabel>
          </RowText>
          <MovieceptionSwitch
            checked={settings?.notifications.newSeasonAlerts ?? false}
            onChange={(e) => onChange('newSeasonAlerts', e.target.checked)}
            disabled={typeDisabled}
          />
        </Row>
        <Row>
          <RowText>
            <RowLabel>Recommendations</RowLabel>
          </RowText>
          <MovieceptionSwitch
            checked={settings?.notifications.recommendations ?? false}
            onChange={(e) => onChange('recommendations', e.target.checked)}
            disabled={typeDisabled}
          />
        </Row>
        <Row>
          <RowText>
            <RowLabel>New Releases</RowLabel>
          </RowText>
          <MovieceptionSwitch
            checked={settings?.notifications.newReleases ?? false}
            onChange={(e) => onChange('newReleases', e.target.checked)}
            disabled={typeDisabled}
          />
        </Row>
        <Row>
          <RowText>
            <RowLabel>Comment Replies</RowLabel>
          </RowText>
          <MovieceptionSwitch
            checked={settings?.notifications.commentReplies ?? false}
            onChange={(e) => onChange('commentReplies', e.target.checked)}
            disabled={typeDisabled}
          />
        </Row>
        <Row>
          <RowText>
            <RowLabel>Activity &amp; Reminders</RowLabel>
          </RowText>
          <MovieceptionSwitch
            checked={settings?.activityReminders ?? false}
            onChange={(e) =>
              onTopLevelToggle('activityReminders', e.target.checked)
            }
            disabled={typeDisabled}
          />
        </Row>
        <Row>
          <RowText>
            <RowLabel>Recap &amp; Milestones</RowLabel>
          </RowText>
          <MovieceptionSwitch
            checked={settings?.recapAndMilestones ?? false}
            onChange={(e) =>
              onTopLevelToggle('recapAndMilestones', e.target.checked)
            }
            disabled={typeDisabled}
          />
        </Row>
        <Row>
          <RowText>
            <RowLabel>Email Frequency</RowLabel>
          </RowText>
          <FrequencySelect
            size='small'
            value={settings?.notifications.notificationFrequency ?? 'daily'}
            onChange={(e) => onChange('notificationFrequency', e.target.value)}
            disabled={typeDisabled}
          >
            <MenuItem value='daily'>Daily</MenuItem>
            <MenuItem value='weekly'>Weekly</MenuItem>
          </FrequencySelect>
        </Row>
        <Row>
          <RowText>
            <RowLabel>Quiet Hours</RowLabel>
          </RowText>
          <MovieceptionSwitch
            checked={quietHours.enabled}
            onChange={(e) =>
              onChange('quietHours', {
                ...quietHours,
                enabled: e.target.checked,
              })
            }
            disabled={typeDisabled}
          />
        </Row>
        {quietHours.enabled && (
          <Row>
            <RowText>
              <RowLabel>No Emails Between</RowLabel>
            </RowText>
            <QuietHoursRange>
              <TextField
                type='time'
                size='small'
                value={quietHours.start}
                onChange={(e) =>
                  onChange('quietHours', {
                    ...quietHours,
                    start: e.target.value,
                  })
                }
                disabled={typeDisabled}
              />
              <TextField
                type='time'
                size='small'
                value={quietHours.end}
                onChange={(e) =>
                  onChange('quietHours', {
                    ...quietHours,
                    end: e.target.value,
                  })
                }
                disabled={typeDisabled}
              />
            </QuietHoursRange>
          </Row>
        )}
      </Box>
    </React.Fragment>
  )
}
